import Script from 'next/script';
import { useCallback, useMemo } from 'react';
import { absoluteBaseUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import {
  FacebookIcon,
  FacebookButton,
  Wrapper,
  TwitterIcon,
  TwitterIntegrationButton
} from './Buttons.styles';

declare global {
  interface Window {
    FB: {
      ui: (
        params: {
          method: string;
          href: string;
          hashtag: string;
          quote: string;
        },
        callback: (response?: { error_message: unknown }) => void
      ) => void;
    };
  }
}

function shareableUrlConstructor(assetId: string, socialMediaSource: string) {
  return `${absoluteBaseUrl}/shareyourcarvana/${assetId}?utm_source=${socialMediaSource}SYC&utm_medium=sharing&utm_campaign=ShareYourCarvana&utm_content=${assetId}&utm_term=0`;
}

type OwnProps = {
  assetId: string;
};

export default function SocialMediaButtons({ assetId }: OwnProps) {
  const trackEvent = useTrackEvent();

  const twitterShareLink = useMemo(() => {
    const twitterSharableUrl = shareableUrlConstructor(assetId, 'Twitter');

    return encodeURI(
      `https://twitter.com/intent/tweet?&text=My new ride from @Carvana!&hashtags=shareyourcarvana&url=${twitterSharableUrl}`
    );
  }, [assetId]);

  const handleFacebook = useCallback(() => {
    window.FB.ui(
      {
        method: 'share',
        href: shareableUrlConstructor(assetId, 'Facebook'),
        hashtag: '#shareyourcarvana',
        quote: 'My new ride from @CARVANA.BuyCarsOnline!'
      },
      (response?: { error_message: unknown }) => {
        if (response && !response.error_message) {
          trackEvent('Facebook shared successful');
        } else {
          trackEvent('Facebook shared unsuccessful');
        }
      }
    );
  }, [assetId, trackEvent]);

  return (
    <>
      <Script
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: `
window.fbAsyncInit = function() {
  FB.init({
    appId: 404528826297797,
    autoLogAppEvents: true,
    xfbml: true,
    version: "v4.0"
  });
}
      `
        }}
      />
      <Script
        src="https://connect.facebook.net/en_US/sdk.js"
        strategy="afterInteractive"
      />
      <Wrapper>
        <FacebookButton
          onClick={handleFacebook}
          data-analytics-id="Share on Facebook button"
          data-testid="share-facebook"
        >
          <FacebookIcon width={26} height={26} />
          Share On Facebook
        </FacebookButton>
        <a
          href={twitterShareLink}
          target="_blank"
          rel="noopener noreferrer"
          data-analytics-id="Share on Twitter button"
          data-testid="share-twitter"
        >
          <TwitterIntegrationButton>
            <TwitterIcon width={29} height={24} />
            Share On Twitter
          </TwitterIntegrationButton>
        </a>
      </Wrapper>
    </>
  );
}
